import { useCallback } from 'react';
import {
  makeNavigationParams,
  navigationEventEmitter,
  type Parser,
  type RouteDefinition,
  type maybeOptionalParserParams,
  navigateEvent,
  type NavigateOptions,
} from '@epihub/core/src/routes';
// @ts-ignore
import { type L as Location } from 'react-router/dist/development/context-jKip1TFB';
import { useLatestRef } from '../hooks/useLatestRef';

export function useNavigateTo<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>) {
  const dataRef = useLatestRef(route);

  return useCallback((
    destination?:
      & maybeOptionalParserParams<'path', ParsePath>
      & maybeOptionalParserParams<'hash', ParseHash>
      & maybeOptionalParserParams<'search', ParseSearch>
      & { options?: NavigateOptions },
  ) => {
    return navigateTo(route, destination);
  }, [dataRef]);
}

export function navigateTo<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(
  route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>,
  destination?:
    & maybeOptionalParserParams<'path', ParsePath>
    & maybeOptionalParserParams<'hash', ParseHash>
    & maybeOptionalParserParams<'search', ParseSearch>
    & { options?: NavigateOptions },
) {
  navigationEventEmitter.send(
    navigateEvent,
    makeNavigationOptions(route, destination),
  );
}

export function makeNavigationOptions<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(
  route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>,
  destination?:
    & maybeOptionalParserParams<'path', ParsePath>
    & maybeOptionalParserParams<'hash', ParseHash>
    & maybeOptionalParserParams<'search', ParseSearch>
    & { options?: NavigateOptions },
) {
  return makeNavigationParams(route, destination);
}

export function makeTo<
  Name extends string = string,
  Path extends string = string,
  ParsePath extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseHash extends Record<string, Parser<any>> = Record<string, Parser<any>>,
  ParseSearch extends Record<string, Parser<any>> = Record<string, Parser<any>>,
>(
  route: RouteDefinition<Name, Path, ParsePath, ParseHash, ParseSearch>,
  destination?:
    & maybeOptionalParserParams<'path', ParsePath>
    & maybeOptionalParserParams<'hash', ParseHash>
    & maybeOptionalParserParams<'search', ParseSearch>
    & { options?: NavigateOptions },
) {
  return makeNavigationParams(route, destination).to;
}

export function isCurrentRoute(route: RouteDefinition, location: Location) {
  const matcher = compilePathMatcher(route.path);
  return matcher.test(location.pathname);
}

/**
 * Compiles a route path template into a regex for matching.
 * Handles path parameters like :id, catch-all routes like /files/*, and optional trailing slashes.
 */
function compilePathMatcher(path: string): RegExp {
  const hasCatchAll = /\/\*$/.test(path);

  let regexpSource = '^'
    + path
      .replace(/\/*\*?$/, '') // Ignore trailing / and /*
      .replace(/^\/*/, '/') // Ensure leading /
      .replace(/:(\w+)/g, '([^\\/]+)'); // Replace :param with capture group

  if (hasCatchAll) {
    regexpSource += '(?:\\/.*)?$'; // Match optional / followed by anything (for catch-all)
  } else {
    regexpSource += '\\/*$'; // Match optional trailing slash at end
  }

  return new RegExp(regexpSource, 'i');
}
